<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { useTranslations } from '~/composables/translations';
import Menu from '~/components/FAQ/Menu.vue';
import Content from '~/components/FAQ/Content.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { scroll, scrollToElement } from '~/composables/scroll';

import type { FAQArticle, FAQMenuContent } from '~/models/faq_models';
import { useSettingsStore } from '~/stores/settings';
import SideButton from '~/components/SideButton.vue';
import BackButton from '~/components/BackButton.vue';

const props = defineProps<{
  type: string;
  content: FAQMenuContent[];
  routerArticles: string;
}>();

const route = useRoute();
const id = ref(route.params.id);
const settings = useSettingsStore();

watch(
  () => route.params.id,
  async () => {
    id.value = route.params.id;
  },
);
const { $i18n } = useTranslations(`faq_${props.type}`);
const findArticleCondition = (el: any) =>
  el.articles.find((e: FAQArticle) => e.url === id.value || e.alias === id.value);
const currentContent = computed(() => props.content.find(findArticleCondition));
const router = useRouter();

const redirect = (value: string) => {
  if (currentContent.value?.articles.find((el) => el.url === value)) {
    const element = document.querySelector(`h3[data-title=${value}]`) as HTMLAnchorElement;
    scrollToElement(element, 20);
  } else {
    scroll(0);
  }

  router.push({ name: props.routerArticles, params: { id: value } });
};

onMounted(() => {
  setTimeout(() => {
    const element = document.querySelector(`h3[data-title=${id.value}]`) as HTMLAnchorElement;
    scrollToElement(element, 20);
  }, 500);
});
</script>
<template>
  <div class="mp-container mpf-faq-menu flex flex-column">
    <div
      class="flex"
      :class="{
        'justify-space-between': settings.isAddinMode,
        'justify-center': !settings.isAddinMode,
      }"
    >
      <BackButton v-if="settings.isAddinMode" class="back" @back="$router.back()" />
      <h1>
        {{ $i18n('faqTitle') }}
        -
        {{ $i18n('faqServiceName') }}
      </h1>
      <div v-if="settings.isAddinMode" class="spacer" />
    </div>
    <Menu
      :active-content="currentContent"
      :content="props.content"
      :module="`faq_${props.type}`"
      @redirect="redirect"
      :is-responsive="true"
      v-if="settings.isMobileDevice"
    />
    <div class="faq-container flex flex-row grid-gap-10px relative">
      <Menu
        :active-content="currentContent"
        :content="props.content"
        :module="`faq_${props.type}`"
        @redirect="redirect"
        :is-responsive="false"
        v-if="!settings.isMobileDevice"
      />
      <Content
        :active-content="currentContent"
        :content="props.content"
        :module="`faq_${props.type}`"
      />
      <SideButton class="scroll-to-top-button" @click="scroll(0)">
        <span class="fonticon fonticon-up" />
      </SideButton>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

h1 {
  margin-top: 30px;
  margin-bottom: 35px;
  font-weight: bold;
  line-height: 1.1;
  color: inherit;
  font-size: 36px;

  @include variables.for-sm {
    font-size: 30px;
  }
  @include variables.for-xs {
    font-size: 20px;
  }
}

.faq-container {
  border-top: 1px solid #e0e0e0;
  border-radius: 2px;
}

.back {
  margin-top: 30px;
  margin-left: 5px;
}

.mp-container {
  @include variables.for-sm {
    padding: 0;
  }
}

.scroll-to-top-button {
  bottom: 120px;

  @include variables.for-sm {
    bottom: 80px;
  }
}
</style>
